import { useState } from "react";
import { Field, Label, Switch } from "@headlessui/react";
import { LockClosedIcon, PhotoIcon } from "@heroicons/react/24/outline";
import {
	SchoolSpaViewModel,
	useUpdateUserAvatarSettingForSchoolMutation,
} from "../schoolsApiSlice";

interface UserAvatarSettingProps {
	schoolInfo: SchoolSpaViewModel;
	setSchoolInfo: (schoolInfo: SchoolSpaViewModel) => void;
	setErrorMessage: (message: string) => void;
}

export default function UserAvatarSetting({
	schoolInfo,
	setSchoolInfo,
	setErrorMessage,
}: UserAvatarSettingProps) {
	const [disabled, setDisabled] = useState(schoolInfo.disableUserImages);

	const [callUpdate] = useUpdateUserAvatarSettingForSchoolMutation();

	function handleChange() {
		callUpdate({ schoolId: schoolInfo.id, disabled: !disabled })
			.unwrap()
			.then(() => {
				setErrorMessage("");
				setDisabled(!disabled);
				setSchoolInfo({ ...schoolInfo, disableUserImages: !disabled });
			})
			.catch((error) => {
				setErrorMessage(`Failed to save the user avatar setting. ${error.data}`);
			});
	}

	return (
		<div className="flex items-center">
			<PhotoIcon className="size-18" />
			<div className="ml-4">
				<p>Allow User Avatars</p>
				<div className="mt-1">
					{schoolInfo.disableUserImagesByDistrict ? (
						<div className="flex items-center ml-2">
							<Switch
								checked={false}
								disabled={true}
								name="userAvatar"
								className="group relative inline-flex items-center h-7 w-12 shrink-0 rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden">
								<span
									aria-hidden="true"
									className="pointer-events-none absolute inset-0 flex items-center justify-center size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5">
									<LockClosedIcon className="size-5 items-center text-gray-500" />
								</span>
							</Switch>
							<p className="ml-2 text-sm">Disabled by the district.</p>
						</div>
					) : (
						<Field className="flex items-center ml-2">
							<Label className="sr-only">User Avatar Toggle</Label>
							<Switch
								checked={!disabled}
								onChange={handleChange}
								name="userAvatar"
								className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
								<span
									aria-hidden="true"
									className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
								/>
							</Switch>
						</Field>
					)}
				</div>
			</div>
		</div>
	);
}
